<script setup lang="ts">
import { computed } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import { useUiStore } from '@/stores/ui'

const props = defineProps<{
    name: string
    count: number | string
    labels: string[]
    values: number[]
}>()

const ui = useUiStore()

const series = computed(() => [{ name: props.name, data: props.values }])

const chartOptions = computed(() => {
    const isDark = ui.resolvedTheme === 'dark'
    return {
        chart: {
            type: 'area' as const,
            toolbar: { show: false },
            zoom: { enabled: false },
            animations: { enabled: false },
            fontFamily: 'inherit'
        },
        colors: [isDark ? '#2dd4bf' : '#007F74'],
        stroke: { curve: 'smooth' as const, width: 2 },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.25,
                opacityTo: 0.02,
                stops: [0, 100]
            }
        },
        dataLabels: { enabled: false },
        grid: {
            borderColor: isDark ? '#2d333b' : 'rgba(0,0,0,0.05)',
            strokeDashArray: 0,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
            padding: { left: 0, right: 8 }
        },
        xaxis: {
            categories: props.labels,
            labels: {
                style: { colors: isDark ? '#adbac7' : '#6b7280', fontSize: '10px' },
                rotate: 0,
                hideOverlappingLabels: true
            },
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: {
            labels: {
                style: { colors: isDark ? '#adbac7' : '#6b7280', fontSize: '10px' },
                formatter: (v: number) => Math.round(v).toString()
            }
        },
        tooltip: {
            theme: 'dark',
            x: { show: true },
            y: { formatter: (v: number) => `${v}` },
            marker: { show: false }
        }
    }
})
</script>

<template>
    <div class="bg-white border border-gray-200 rounded-12 p-4">
        <div class="flex items-baseline justify-between mb-2">
            <div class="text-sm font-semibold text-ink truncate">{{ name }}</div>
            <div class="text-xs text-gray-500 shrink-0 ml-2 tabular-nums">{{ count }}</div>
        </div>
        <div class="h-[180px]">
            <VueApexCharts type="area" height="180" :options="chartOptions" :series="series" />
        </div>
    </div>
</template>
